// src/utils/voicePrompts.js
// Piano With Pan — voice narration helper using the Web Speech API.
//
// One thin wrapper around window.speechSynthesis. The rest of the app should
// only ever call:
//   - speak(text, opts)        → speak a line out loud
//   - cancelSpeech()           → stop everything (call on unmount / part change)
//   - isVoiceEnabled()         → read the persisted on/off flag
//   - setVoiceEnabled(bool)    → flip the flag (persisted in localStorage)
//   - onVoiceEnabledChange(fn) → subscribe so UI toggles stay in sync
//
// Future upgrade path: replace the body of speak() with an ElevenLabs / MP3
// player. The rest of the app doesn't need to change.

const LS_KEY = "pwp_voice_enabled_v1";

/* ───────── persisted on/off flag ───────── */

export function isVoiceEnabled() {
  try {
    const v = localStorage.getItem(LS_KEY);
    // Default: ON for first-time users
    if (v === null) return true;
    return v === "1";
  } catch {
    return true;
  }
}

const listeners = new Set();
export function setVoiceEnabled(on) {
  try { localStorage.setItem(LS_KEY, on ? "1" : "0"); } catch {}
  if (!on) cancelSpeech();
  listeners.forEach(fn => { try { fn(on); } catch {} });
}
export function onVoiceEnabledChange(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/* ───────── voice selection — kind female English voice ───────── */

let cachedVoice = null;
let voicesReady = false;

function pickFemaleEnglishVoice() {
  if (typeof window === "undefined" || !window.speechSynthesis) return null;
  const voices = window.speechSynthesis.getVoices() || [];
  if (!voices.length) return null;

  // Preferred names in order — these are common female English voices across
  // Chrome / Safari / Edge / iOS / Android.
  const preferred = [
    "Google UK English Female",
    "Microsoft Aria Online (Natural) - English (United States)",
    "Microsoft Jenny Online (Natural) - English (United States)",
    "Microsoft Sonia Online (Natural) - English (United Kingdom)",
    "Samantha",
    "Karen",
    "Tessa",
    "Moira",
    "Fiona",
    "Serena",
    "Victoria",
    "Allison",
    "Ava",
  ];
  for (const name of preferred) {
    const v = voices.find(v => v.name === name);
    if (v) return v;
  }

  // Heuristic: any English voice whose name contains a feminine word
  const femaleHints = ["female", "woman", "girl", "aria", "jenny", "sonia", "samantha", "karen", "tessa", "moira", "fiona", "serena", "victoria", "allison", "ava", "zira"];
  const enFemale = voices.find(v =>
    /^en[-_]/i.test(v.lang || "") &&
    femaleHints.some(h => (v.name || "").toLowerCase().includes(h))
  );
  if (enFemale) return enFemale;

  // Fallback: first English voice
  const anyEn = voices.find(v => /^en[-_]/i.test(v.lang || ""));
  return anyEn || voices[0] || null;
}

function ensureVoice() {
  if (cachedVoice) return cachedVoice;
  cachedVoice = pickFemaleEnglishVoice();
  return cachedVoice;
}

// Voices load asynchronously in Chrome — listen and re-pick
if (typeof window !== "undefined" && window.speechSynthesis) {
  const sync = () => {
    voicesReady = true;
    cachedVoice = pickFemaleEnglishVoice();
  };
  window.speechSynthesis.onvoiceschanged = sync;
  // Kick the engine so onvoiceschanged fires
  try { window.speechSynthesis.getVoices(); } catch {}
  // Some browsers populate synchronously
  if ((window.speechSynthesis.getVoices() || []).length) sync();
}

/* ───────── speak ───────── */

let speakQueue = []; // queued utterances when voices aren't loaded yet
let lastSpokenAt = 0;

/**
 * Speak a line out loud.
 * @param {string} text  - what to say
 * @param {object} opts  - { rate=0.95, pitch=1.1, interrupt=true, priority=false }
 *   interrupt=true cancels anything currently speaking (default — keeps things
 *   snappy for kids; we don't want praise lines stacking up)
 *   priority=true skips the "min gap between utterances" debounce
 */
export function speak(text, opts = {}) {
  if (!text) return;
  if (!isVoiceEnabled()) return;
  if (typeof window === "undefined" || !window.speechSynthesis) return;

  const {
    rate = 0.8,
    pitch = 1.1,
    volume = 1,
    interrupt = true,
    priority = false,
  } = opts;

  // Debounce: don't fire more than one praise per ~250ms during fast play
  if (!priority) {
    const now = Date.now();
    if (now - lastSpokenAt < 250) return;
    lastSpokenAt = now;
  } else {
    lastSpokenAt = Date.now();
  }

  const doSpeak = () => {
    try {
      if (interrupt) window.speechSynthesis.cancel();
      const u = new SpeechSynthesisUtterance(text);
      const v = ensureVoice();
      if (v) u.voice = v;
      u.rate = rate;
      u.pitch = pitch;
      u.volume = volume;
      u.lang = (v && v.lang) || "en-US";
      window.speechSynthesis.speak(u);
    } catch {}
  };

  // If voices aren't ready yet, queue once
  if (!voicesReady && !(window.speechSynthesis.getVoices() || []).length) {
    speakQueue.push(doSpeak);
    setTimeout(() => {
      while (speakQueue.length) {
        const fn = speakQueue.shift();
        try { fn(); } catch {}
      }
    }, 300);
    return;
  }
  doSpeak();
}

/* ───────── cancel ───────── */

export function cancelSpeech() {
  try { window.speechSynthesis && window.speechSynthesis.cancel(); } catch {}
  speakQueue = [];
}

/* ───────── ready-made line builders ───────── */

/** Spell a sequence aloud: ["C","D","E"] → "C, then D, then E" */
export function sequenceToSpeech(sequence) {
  if (!sequence || !sequence.length) return "";
  if (sequence.length === 1) return sequence[0];
  if (sequence.length === 2) return `${sequence[0]}, then ${sequence[1]}`;
  return sequence.slice(0, -1).join(", then ") + ", then " + sequence[sequence.length - 1];
}

/** Build the "find this note" intro line for a prompt */
export function buildPromptIntro(sequence) {
  if (!sequence || !sequence.length) return "";
  if (sequence.length === 1) {
    return `Find the note ${sequence[0]} and play it.`;
  }
  return `Now play ${sequenceToSpeech(sequence)}.`;
}

/** Praise for an individual correct sub-note (short) */
const SHORT_PRAISE = ["Good!", "Yes!", "Nice!", "That's it!"];
export function praiseSubNote() {
  return SHORT_PRAISE[Math.floor(Math.random() * SHORT_PRAISE.length)];
}

/** Praise for finishing a whole prompt */
const PROMPT_PRAISE = ["Excellent!", "Amazing!", "You got it!", "Wonderful!"];
export function praisePromptComplete() {
  return PROMPT_PRAISE[Math.floor(Math.random() * PROMPT_PRAISE.length)];
}

/** Build a wrong-note feedback line (matches the bubble text) */
export function buildWrongFeedback(played, expected) {
  if (!played || !expected) return "Try again!";
  const NOTE_ORDER = ["C","D","E","F","G","A","B"];
  const pi = NOTE_ORDER.indexOf(played);
  const ei = NOTE_ORDER.indexOf(expected);
  if (pi < 0 || ei < 0) return `That was ${played}. Try ${expected}.`;
  const dist = Math.abs(pi - ei);
  if (dist === 1) return `Oh so close! That's ${played}. ${expected} is right next to it.`;
  if (dist === 2) return `That's ${played}. ${expected} is two keys away.`;
  return `That was ${played}. Look for ${expected}.`;
}

/** Build the "part complete" line */
export function buildPartCompleteLine(partTitle, nextTitle) {
  if (nextTitle) {
    return `Wonderful! You finished ${partTitle}. Let's try ${nextTitle} next!`;
  }
  return `Amazing! You finished ${partTitle}. You're ready for Practice Mode!`;
}